import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Text, VStack, Spinner, Alert, AlertIcon } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { Input, FormControl, FormLabel, FormErrorMessage, HStack, Tag } from '@chakra-ui/react';
import { TagsInput } from "react-tag-input-component";
import PrimaryButton from './themeComponents/PrimaryButton';
import { sendTestWhatsapp, sendTestEmail, sendTestEmailMulti, sendTestSms } from '../Services/api';
import { useToast } from '@chakra-ui/react';
import { useQuery } from 'react-query';
import * as _ from 'lodash';
import axiosInstance from '../Services/axiosInstance';

function SendTestModal({ 
    isOpen, 
    onClose, 
    channel, 
    uuid, 
    orgToken, 
    templateData, 
    selectedSmtpUuid, 
    formik, 
    targetedAudience 
}) {
    const toast = useToast();
    const [email, setEmail] = useState("");
    const [phones, setPhones] = useState([]);
    const [error, setError] = useState(false);
    const [selectedSmtp, setSelectedSmtp] = useState(""); // Only used in single-org mode
    const [intersaction, setIntersaction] = useState([]);

    const isEmailChannel = channel?.toLowerCase() === 'email';
    const isAllBanksMode = window?.location?.href?.includes("all-banks");

    // Fetch SMTP Providers (only for single-org email)
    const { data: smtpData, isLoading: smtpLoading, isError: smtpError } = useQuery(
        ['smtp-providers', orgToken],
        async () => {
            const response = await axiosInstance.get(`/account/rest/v1/email-channels`);
            return response.data;
        },
        {
            enabled: isEmailChannel && !isAllBanksMode, // Don't fetch in all-banks mode
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
        }
    );

    // Set default SMTP only in single-org mode
    useEffect(() => {
        if (smtpData && isEmailChannel && !isAllBanksMode) {
            const providers = smtpData.data || [];
            if (providers.length === 0) {
                setSelectedSmtp("");
                return;
            }

            let defaultUuid = smtpData.default_transactional_uuid || smtpData.default_promotional_uuid;
            if (defaultUuid && providers.find(p => p.uuid === defaultUuid)) {
                setSelectedSmtp(defaultUuid);
            } else {
                const enabledProvider = providers.find(p => p.enabled);
                setSelectedSmtp(enabledProvider?.uuid || providers[0].uuid);
            }
        }
    }, [smtpData, isEmailChannel, isAllBanksMode]);

    // Extract common keys across all banks for sample data hint
    useEffect(() => {
        if (isAllBanksMode && targetedAudience) {
            const allKeys = Object.values(targetedAudience)
                .flatMap(org => org.segment || [])
                .filter(Boolean)
                .flatMap(segment => 
                    (segment?.data?.aggregates?.keys || segment?.aggregates?.keys || [])
                        .map(k => k.label)
                )
                .filter(Boolean);

            const uniqueKeys = _.uniq(allKeys);
            setIntersaction(uniqueKeys);
        }
    }, [targetedAudience, isAllBanksMode]);

    const validateEmail = (value) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        const valid = re.test(String(value).toLowerCase());
        setError(!valid);
        return valid;
    };

    const validatePhone = (value) => {
        const re = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
        const valid = re.test(value) || /^\d{10,15}$/.test(value.replace(/\D/g, ''));
        setError(!valid);
        return valid;
    };

    const sendTestCommunication = async () => {
        if (isEmailChannel && !email.trim()) {
            setError(true);
            return;
        }
        if (!isEmailChannel && phones.length === 0) {
            setError(true);
            return;
        }

        try {
            if (channel?.toLowerCase() === "email") {
                if (isAllBanksMode) {
                    // Multi-org: each org uses its own default SMTP → no selectedSmtpUuid needed
                    const preparedTempData = formik.values.orgList.map((org) => ({
                        orgToken: org,
                        csv_url: targetedAudience[org]?.csv?.[0]?.data?.url || null,
                        segment_id: targetedAudience[org]?.segment?.[0]?.data?.id || targetedAudience[org]?.segment?.[0]?.id || null,
                        keys: targetedAudience[org]?.segment?.[0]?.data?.aggregates?.keys || targetedAudience[org]?.segment?.[0]?.aggregates?.keys || null,
                        templateData: {
                            headerUuid: templateData[org]?.header?.uuid || "",
                            bodyUuid: templateData[org]?.body?.uuid || "",
                            footerUuid: templateData[org]?.footer?.uuid || ""
                        },
                        email: email,
                        subject: formik.values.subjectLine,
                        selectedSmtpUuid:selectedSmtpUuid[org]
                        
                    }));

                    const response = await sendTestEmailMulti({ data: preparedTempData }, orgToken);
                    response.data.forEach((dt) => {
                        toast({
                            title: dt.status === 'success' ? 'Sent!' : 'Failed',
                            description: dt.message || `Test email ${dt.status} for ${dt.orgToken || 'org'}`,
                            status: dt.status === 'success' ? 'success' : 'error',
                            duration: 5000,
                        });
                    });
                } else {
                    // Single org: use selected SMTP
                    if (!selectedSmtp) {
                        toast({ title: 'Error', description: 'Please select an SMTP provider', status: 'error' });
                        return;
                    }

                    const preparedData = {
                        email: email,
                        selectedTemplate: uuid,
                        selectedSmtpUuid: selectedSmtp,
                        broadcastType: 'BROADCAST_V2',
                        csv_url: formik?.values?.targetedAudience?.includes?.CSV?.[0]?.data?.url || null,
                        segment_id: formik?.values?.targetedAudience?.includes?.segment?.[0]?.id || null,
                        keys: formik?.values?.targetedAudience?.includes?.segment?.[0]?.aggregates?.keys || null,
                    };

                    const response = await sendTestEmail(preparedData, orgToken);
                    toast({
                        title: response.data.status === 'success' ? 'Sent!' : 'Failed',
                        description: response.data.message || 'Test email sent',
                        status: response.data.status === 'success' ? 'success' : 'error',
                    });
                }
                onClose();

            } else if (channel?.toLowerCase() === "whatsapp") {
                // WhatsApp & SMS logic remains unchanged
                const numbers = phones.map(num => ({ phone: num }));
                let data = [];

                if (isAllBanksMode) {
                    formik.values.orgList.forEach((org) => {
                        data.push({
                            orgToken: org,
                            mappedUserPhones: numbers,
                            csv_url: targetedAudience[org]?.csv?.[0]?.data?.url || null,
                            segment_id: targetedAudience[org]?.segment?.[0]?.data?.id || null,
                            keys: targetedAudience[org]?.segment?.[0]?.data?.aggregates?.keys || null,
                            selectedTemp: uuid || templateData[org]?.uuid,
                        });
                    });
                }

                const payload = isAllBanksMode
                    ? { data }
                    : {
                          mappedUserPhones: numbers,
                          selectedTemp: uuid || templateData[orgToken]?.uuid,
                          csv_url: formik?.values?.targetedAudience?.includes?.CSV?.[0]?.data?.url || null,
                          segment_id: formik?.values?.targetedAudience?.includes?.segment?.[0]?.id || null,
                          keys: formik?.values?.targetedAudience?.includes?.segment?.[0]?.aggregates?.keys || null,
                      };

                const response = await sendTestWhatsapp(payload, orgToken);
                const respData = Array.isArray(response.data) ? response.data : [response.data];

                respData.forEach(dt => {
                    toast({
                        title: dt.status === 'success' ? 'Sent!' : 'Failed',
                        description: dt.message || 'Test WhatsApp',
                        status: dt.status === 'success' ? 'success' : 'error',
                    });
                });
                onClose();

            } else if (channel?.toLowerCase() === "sms") {
                const destination = phones.map(num => ({ phone: num }));
                let preparedTempData = [];

                if (isAllBanksMode) {
                    formik.values.orgList.forEach((org) => {
                        preparedTempData.push({
                            orgToken: org,
                            selectedTemp: uuid || templateData[org]?.uuid,
                            destination,
                            approvedTemplateId: "",
                            csv_url: targetedAudience[org]?.csv?.[0]?.data?.url || null,
                            segment_id: targetedAudience[org]?.segment?.[0]?.data?.id || null,
                            keys: targetedAudience[org]?.segment?.[0]?.data?.aggregates?.keys || null,
                        });
                    });
                }

                const payload = isAllBanksMode
                    ? { data: preparedTempData }
                    : {
                          selectedTemp: uuid || templateData[orgToken]?.uuid,
                          destination,
                          csv_url: formik?.values?.targetedAudience?.includes?.CSV?.[0]?.data?.url || null,
                          segment_id: formik?.values?.targetedAudience?.includes?.segment?.[0]?.id || null,
                          keys: formik?.values?.targetedAudience?.includes?.segment?.[0]?.aggregates?.keys || null,
                      };

                const response = await sendTestSms(payload, orgToken);
                const respData = Array.isArray(response.data) ? response.data : [response.data];

                respData.forEach(test => {
                    toast({
                        title: test.status === 'success' ? 'Sent!' : 'Failed',
                        description: test.message || 'Test SMS',
                        status: test.status === 'success' ? 'success' : 'error',
                    });
                });
                onClose();
            }
        } catch (err) {
            console.error("Test send error:", err);
            toast({
                title: 'Error',
                description: `Failed to send test ${channel.toLowerCase()}`,
                status: 'error',
            });
        }
    };

    return (
        <Modal isOpen={isOpen} size={'xl'} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text>Send Test {channel}</Text>
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody pt={0}>
                    <VStack spacing={5} w={'100%'}>
                        {/* Email Input */}
                        {isEmailChannel ? (
                            <FormControl isInvalid={error && !email.trim()}>
                                <FormLabel>Email Address</FormLabel>
                                <Input
                                    value={email}
                                    placeholder='Enter email address'
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyUp={(e) => validateEmail(e.target.value)}
                                />
                                {error && !email.trim() && <FormErrorMessage>Please enter a valid email</FormErrorMessage>}
                            </FormControl>
                        ) : (
                            <FormControl isInvalid={error && phones.length === 0}>
                                <FormLabel>Phone Numbers</FormLabel>
                                <TagsInput
                                    value={phones}
                                    onChange={setPhones}
                                    name="phones"
                                    placeHolder="Enter phone numbers (e.g., +919876543210)"
                                    beforeAddValidate={validatePhone}
                                />
                                {error && phones.length === 0 && <FormErrorMessage>Enter at least one valid phone number</FormErrorMessage>}
                            </FormControl>
                        )}

                        {/* SMTP Provider Selection - Hidden in all-banks mode */}
                        {isEmailChannel && !isAllBanksMode && (
                            <FormControl>
                                <FormLabel>SMTP Provider</FormLabel>
                                {smtpLoading ? (
                                    <HStack>
                                        <Spinner size="sm" />
                                        <Text>Loading providers...</Text>
                                    </HStack>
                                ) : smtpError ? (
                                    <Alert status="error" fontSize="sm">
                                        <AlertIcon />
                                        Failed to load SMTP providers
                                    </Alert>
                                ) : smtpData?.data?.length > 0 ? (
                                    <Select
                                        value={selectedSmtp}
                                        onChange={(e) => setSelectedSmtp(e.target.value)}
                                        placeholder="Select SMTP provider"
                                    >
                                        {smtpData.data.map((provider) => (
                                            <option key={provider.uuid} value={provider.uuid}>
                                                {provider.name?.replace(/"/g, '') || provider.uuid} {provider.enabled ? '' : '(Disabled)'}
                                            </option>
                                        ))}
                                    </Select>
                                ) : (
                                    <Text color="red.500" fontSize="sm">No SMTP providers available</Text>
                                )}
                            </FormControl>
                        )}

                        {/* Show sample keys in all-banks mode */}
                        {isAllBanksMode && intersaction.length > 0 && (
                            <>
                                <Text fontSize="sm" alignSelf="flex-start" color="gray.600">
                                    Sample data will be generated using these common keys:
                                </Text>
                                <HStack flexWrap="wrap" spacing={2}>
                                    {intersaction.map((key) => (
                                        <Tag key={key} size="sm" colorScheme="blue" variant="solid">
                                            {key}
                                        </Tag>
                                    ))}
                                </HStack>
                            </>
                        )}
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <PrimaryButton
                        label="Send Test"
                        onClick={sendTestCommunication}
                        isDisabled={
                            smtpLoading || 
                            (isEmailChannel && !isAllBanksMode && !selectedSmtp) ||
                            (isEmailChannel && !email.trim()) ||
                            (!isEmailChannel && phones.length === 0)
                        }
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default SendTestModal;